import React, { useEffect, useState } from "react";
import "./ChatDrawer.css";

import MahalAI from "../../images/mahalai5.png";
import AIProductCard from "./AIProductCard";
import AISupplierProductCard from "./AISupplierProductCard";


const ChatDrawer = ({
  onClose,
  mode = "documentation"
}) => {

  // =========================================================
  // STATE
  // =========================================================

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [messages, setMessages] = useState([]);

  // Shopping products
  const [products, setProducts] = useState([]);

  // Supplier analytics products
  const [supplierProducts, setSupplierProducts] = useState([]);

  // Supplier product-selection results
  const [supplierProductSelection, setSupplierProductSelection] = useState([]);

  // =========================================================
  // SUPPLIER CONVERSATION ID
  // =========================================================
  //
  // This ID identifies the current chat conversation.
  //
  // Example:
  //
  // Message 1:
  // "How many pending orders?"
  //
  // conversation_id = same ID
  //
  // Message 2:
  // "Then in September?"
  //
  // conversation_id = SAME ID
  //
  // This allows the backend to remember the previous
  // supplier intent.
  // =========================================================

  const [conversationId] = useState(() => {

    if (
      typeof crypto !== "undefined" &&
      crypto.randomUUID
    ) {
      return crypto.randomUUID();
    }

    return `conversation-${Date.now()}`;
  });


  // =========================================================
  // CONSTANTS
  // =========================================================

  const API_URL =
    "https://api.mahal.qa/api/chat";

  const PROMOTIONS_URL =
    "https://api.mahal.qa/api/promotions";


  // =========================================================
  // INITIAL GREETING
  // =========================================================

  useEffect(() => {

    // Clear product states whenever mode changes
    setProducts([]);
    setSupplierProducts([]);
    setSupplierProductSelection([]);


    // =====================================================
    // SUPPLIER
    // =====================================================

    if (mode === "supplier") {

      setMessages([
        {
          sender: "assistant",
          text:
            "👋 Hi!\n\n" +
            "I'm Mahal AI.\n\n" +
            "How can I help you with your business today?"
        }
      ]);

      return;
    }


    // =====================================================
    // DOCUMENTATION
    // =====================================================

    if (mode === "documentation") {

      setMessages([
        {
          sender: "assistant",
          text:
            "👋 Hi!\n\n" +
            "I'm Mahal AI.\n\n" +
            "How can I help you today?"
        }
      ]);

      return;
    }


    // =====================================================
    // SHOPPING
    // =====================================================

    setMessages([
      {
        sender: "assistant",
        text:
          "👋 Hi!\n\n" +
          "I'm Mahal AI.\n\n" +
          "Looking for products today?"
      }
    ]);

  }, [mode]);


  // =========================================================
  // SUGGESTIONS
  // =========================================================

  const documentationSuggestions = [
    "How do I create a supplier account?",
    "How do I register my restaurant?",
    "How do I add a menu?"
  ];


  const shoppingSuggestions = [
    "Show rice",
    "Show cooking oil",
    "Show today's offers"
  ];


  const supplierSuggestions = [
    "How much revenue did I make this month?",
    "What are my top selling products?",
    "Do I have any pending orders?",
    "Which products need my attention?"
  ];


  const suggestions =
    mode === "supplier"
      ? supplierSuggestions
      : mode === "shopping"
        ? shoppingSuggestions
        : documentationSuggestions;


  // =========================================================
  // LOAD PROMOTIONS
  // =========================================================

  /*
    Kept for future/manual use.

    We do NOT automatically call this when
    the drawer opens.

    Promotions are loaded when the backend returns:

    {
      "type": "promotions"
    }
  */

  const loadPromotions = async () => {

    try {

      const response = await fetch(
        PROMOTIONS_URL
      );

      const data =
        await response.json();

      if (data.success) {

        setProducts(
          data.products || []
        );

      }

    } catch (error) {

      console.error(
        "Promotion loading error:",
        error
      );

    }

  };


  // =========================================================
  // PERIOD FORMATTER
  // =========================================================

  const getPeriodText = (intentData) => {

    if (!intentData) {
      return "";
    }


    const period =
      intentData.period;


    // =====================================================
    // ALL TIME
    // =====================================================

    if (
      !period ||
      period === "all"
    ) {

      return "";
    }


    // =====================================================
    // TODAY
    // =====================================================

    if (period === "today") {

      return "today";
    }


    // =====================================================
    // YESTERDAY
    // =====================================================

    if (period === "yesterday") {

      return "yesterday";
    }


    // =====================================================
    // LAST N MONTHS
    // =====================================================

    if (period === "last_n_months") {

      const months =
        Number(
          intentData.months
        );

      if (
        !months ||
        months <= 0
      ) {

        return "";
      }

      return `the last ${months} months`;
    }


    // =====================================================
    // MONTH
    // =====================================================

    if (
      period === "month" ||
      period === "this_month" ||
      period === "last_month"
    ) {

      const month =
        Number(
          intentData.month
        );

      const year =
        Number(
          intentData.year
        );


      if (
        !month ||
        !year
      ) {

        return "";
      }


      const date =
        new Date(
          year,
          month - 1,
          1
        );


      const monthName =
        date.toLocaleString(
          "en-US",
          {
            month: "long"
          }
        );


      return `${monthName} ${year}`;
    }


    return "";
  };


  // =========================================================
  // ORDER RESPONSE FORMATTER
  // =========================================================

  const formatOrderResponse = (
    intentData,
    result
  ) => {

    const status =
      result?.status ||
      intentData?.status;


    const count =
      Number(
        result?.order_count || 0
      );


    const periodText =
      getPeriodText(intentData);


    // =====================================================
    // STATUS TEXT
    // =====================================================

    const statusMessages = {

      PENDING:
        "pending",

      PLACED:
        "placed",

      ACCEPTED:
        "accepted",

      PACKED:
        "packed",

      OUT_FOR_DELIVERY:
        "out for delivery",

      DELIVERED:
        "delivered",

      REJECTED:
        "rejected"

    };


    // =====================================================
    // ALL ORDERS
    // =====================================================

    if (status === "ALL") {

      if (periodText) {

        return (
          `You had ${count} ` +
          `${count === 1 ? "order" : "orders"} ` +
          `in ${periodText}.`
        );

      }


      return (
        `You currently have ${count} ` +
        `${count === 1 ? "order" : "orders"}.`
      );

    }


    // =====================================================
    // UNKNOWN STATUS
    // =====================================================

    if (!statusMessages[status]) {

      return (
        "I don't have enough information " +
        "to answer that yet."
      );

    }


    const statusText =
      statusMessages[status];


    // =====================================================
    // STATUS + PERIOD
    // =====================================================

    if (periodText) {

      return (
        `You had ${count} ${statusText} ` +
        `${count === 1 ? "order" : "orders"} ` +
        `in ${periodText}.`
      );

    }


    // =====================================================
    // CURRENT STATUS
    // =====================================================

    return (
      `You currently have ${count} ` +
      `${statusText} ` +
      `${count === 1 ? "order" : "orders"}.`
    );

  };


  // =========================================================
  // REVENUE RESPONSE FORMATTER
  // =========================================================

  const formatRevenueResponse = (
    intentData,
    result
  ) => {

    const totalSales =
      Number(
        result?.total_sales || 0
      );


    const totalOrders =
      Number(
        result?.total_orders || 0
      );


    // -----------------------------------------------------
    // Use result period as fallback.
    //
    // This protects us if the backend puts period
    // inside data instead of intent.
    // -----------------------------------------------------

    const effectiveIntentData = {

      ...(result || {}),

      ...(intentData || {})

    };


    const period =
      effectiveIntentData.period;


    const periodText =
      getPeriodText(
        effectiveIntentData
      );


    // =====================================================
    // FORMAT MONEY
    // =====================================================

    const formattedSales =
      totalSales.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }
      );


    // =====================================================
    // TODAY
    // =====================================================

    if (period === "today") {

      return (
        `Today's sales are QAR ${formattedSales} ` +
        `from ${totalOrders} ` +
        `${totalOrders === 1 ? "order" : "orders"}.`
      );

    }


    // =====================================================
    // YESTERDAY
    // =====================================================

    if (period === "yesterday") {

      return (
        `Yesterday's sales were QAR ${formattedSales} ` +
        `from ${totalOrders} ` +
        `${totalOrders === 1 ? "order" : "orders"}.`
      );

    }


    // =====================================================
    // LAST N MONTHS
    // =====================================================

    if (period === "last_n_months") {

      if (periodText) {

        return (
          `Your sales for ${periodText} are ` +
          `QAR ${formattedSales} ` +
          `from ${totalOrders} ` +
          `${totalOrders === 1 ? "order" : "orders"}.`
        );

      }

    }


    // =====================================================
    // SPECIFIC MONTH
    // =====================================================

    if (
      period === "month" ||
      period === "this_month" ||
      period === "last_month"
    ) {

      if (periodText) {

        return (
          `Your ${periodText} revenue is ` +
          `QAR ${formattedSales} ` +
          `from ${totalOrders} ` +
          `${totalOrders === 1 ? "order" : "orders"}.`
        );

      }

    }


    // =====================================================
    // ALL / CURRENT
    // =====================================================

    return (
      `Your current sales are QAR ${formattedSales} ` +
      `from ${totalOrders} ` +
      `${totalOrders === 1 ? "order" : "orders"}.`
    );

  };


  // =========================================================
  // ADD ASSISTANT MESSAGE
  // =========================================================

  const addAssistantMessage = (text) => {

    setMessages(prev => [

      ...prev,

      {
        sender: "assistant",
        text
      }

    ]);

  };


  // =========================================================
  // SEND MESSAGE
  // =========================================================

  const sendMessage = async (
    question = null
  ) => {

    const userMessage =
      typeof question === "string"
        ? question
        : message;


    if (!userMessage.trim()) {
      return;
    }


    // =====================================================
    // CLEAR PREVIOUS SUPPLIER RESULT
    // =====================================================

    if (mode === "supplier") {

      setSupplierProducts([]);
      setSupplierProductSelection([]);

    }


    // =====================================================
    // ADD USER MESSAGE
    // =====================================================

    setMessages(prev => [

      ...prev,

      {
        sender: "user",
        text: userMessage
      }

    ]);


    setMessage("");
    setLoading(true);


    try {

      // ===================================================
      // AUTH TOKEN
      // ===================================================

      const token =
        localStorage.getItem("token");


      // ===================================================
      // API REQUEST
      // ===================================================

      const response =
        await fetch(
          API_URL,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              "Authorization":
                `Bearer ${token}`
            },

            body:
              JSON.stringify({
                mode,
                message: userMessage,

                // -----------------------------------------
                // IMPORTANT:
                // Send the SAME conversation ID with
                // every message in this chat.
                // -----------------------------------------

                conversation_id:
                  conversationId
              })

          }
        );


      const data =
        await response.json();


      // ===================================================
      // HTTP ERROR
      // ===================================================

      if (!response.ok) {

        addAssistantMessage(
          data.response ||
          data.message ||
          "I don't have enough information to answer that yet."
        );

        return;
      }


      // ===================================================
      // DOCUMENTATION
      // ===================================================

      if (
        data.type === "documentation"
      ) {

        addAssistantMessage(
          data.response ||
          "I don't have enough information to answer that yet."
        );

        return;
      }


      // ===================================================
      // SHOPPING
      // ===================================================

      if (
        data.type === "shopping"
      ) {

        const productList =
          data.products || [];


        setProducts(
          productList
        );


        addAssistantMessage(
          `I found ${productList.length} ` +
          `product(s).`
        );

        return;
      }


      // ===================================================
      // PROMOTIONS
      // ===================================================

      if (
        data.type === "promotions"
      ) {

        const promotionList =
          data.products || [];


        setProducts(
          promotionList
        );


        addAssistantMessage(
          "Here are today's promotions."
        );

        return;
      }


      // ===================================================
      // SUPPLIER PRODUCT SELECTION
      // ===================================================

      if (
        data.type === "product_selection"
      ) {

        const selectionList =
          data.products || [];

        setSupplierProductSelection(
          selectionList
        );

        addAssistantMessage(
          data.message ||
          `I found ${selectionList.length} matching products. Please select one.`
        );

        return;
      }


      // ===================================================
      // SUPPLIER BUSINESS
      // ===================================================

      if (
        data.type === "supplier"
      ) {

        const intentData =
          data.intent || {};


        const intent =
          intentData.intent;


        const result =
          data.data || {};


        let responseText = "";


        // -----------------------------------------------
        // ORDER COUNT
        // -----------------------------------------------

        if (
          intent === "order_count"
        ) {

          responseText =
            formatOrderResponse(
              intentData,
              result
            );

        }


        // -----------------------------------------------
        // REVENUE
        // -----------------------------------------------

        else if (
          intent === "revenue"
        ) {

          responseText =
            formatRevenueResponse(
              intentData,
              result
            );

        }


        // -----------------------------------------------
        // PRODUCT SALES
        // -----------------------------------------------

        else if (
          intent === "product_sales"
        ) {

          const product =
            result.product || {};

          const unitsSold =
            Number(result.units_sold || 0);

          const orderCount =
            Number(result.order_count || 0);

          const revenue =
            Number(result.revenue || 0);

          const productName =
            product.product_name ||
            product.product_name_english ||
            "This product";

          const currency =
            product.currency ||
            "QAR";

          setSupplierProductSelection([]);

          responseText =
            `${productName} has ` +
            `${unitsSold.toLocaleString()} ` +
            `${unitsSold === 1 ? "unit" : "units"} sold ` +
            `from ${orderCount.toLocaleString()} ` +
            `${orderCount === 1 ? "order" : "orders"}, ` +
            `with revenue of ${currency} ` +
            `${revenue.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            })}.`;

        }


        // -----------------------------------------------
        // TOP SELLING PRODUCTS
        // -----------------------------------------------

        else if (
          intent === "top_selling_products"
        ) {

          const supplierProductList =
            result.products || [];


          setSupplierProducts(
            supplierProductList
          );


          if (
            supplierProductList.length > 0
          ) {

            responseText =
              "Here are your top selling products.";

          } else {

            responseText =
              "You don't have any sales data for this period.";

          }

        }


        // -----------------------------------------------
        // UNKNOWN / UNSUPPORTED
        // -----------------------------------------------

        else {

          responseText =
            data.response ||
            "I don't have enough information to answer that yet.";

        }


        addAssistantMessage(
          responseText
        );

        return;
      }


      // ===================================================
      // ERROR TYPE
      // ===================================================

      if (
        data.type === "error"
      ) {

        addAssistantMessage(
          data.response ||
          "I don't have enough information to answer that yet."
        );

        return;
      }


      // ===================================================
      // UNKNOWN RESPONSE
      // ===================================================

      addAssistantMessage(
        data.response ||
        "I don't have enough information to answer that yet."
      );

    }


    // =====================================================
    // CONNECTION ERROR
    // =====================================================

    catch (error) {

      console.error(
        "Mahal AI Error:",
        error
      );


      addAssistantMessage(
        "⚠ Unable to connect to Mahal AI."
      );

    }


    // =====================================================
    // FINISH LOADING
    // =====================================================

    finally {

      setLoading(false);

    }

  };


  // =========================================================
  // UI
  // =========================================================

  return (

    <div className="chat_overlay">

      <div className="chat_drawer">


        {/* =================================================
            HEADER
        ================================================= */}

        <div className="aichat_header">

          <h4 className="aichat_title">

            <img
              src={MahalAI}
              alt="Mahal AI"
              className="mahal_ai_icon"
            />

            <span className="title_text">
              Mahal AI
            </span>

          </h4>


          <button
            onClick={onClose}
            aria-label="Close Mahal AI"
          >
            ✕
          </button>

        </div>


        {/* =================================================
            CHAT BODY
        ================================================= */}

        <div className="chat_body">


          {/* =================================================
              CHAT MESSAGES
          ================================================= */}

          {messages.map(
            (msg, index) => (

              <div
                key={index}
                className={
                  msg.sender === "user"
                    ? "user_message"
                    : "assistant_message"
                }
              >

                {msg.text}

              </div>

            )
          )}


          {/* =================================================
              SHOPPING PRODUCTS
          ================================================= */}

          {mode === "shopping" &&
            products.length > 0 && (

              <div className="shopping_products">

                {products.map(
                  product => (

                    <AIProductCard
                      key={
                        product.product_id
                      }
                      product={product}
                    />

                  )
                )}

              </div>

            )}


          {/* =================================================
              SUPPLIER PRODUCT SELECTION
          ================================================= */}

          {mode === "supplier" &&
            supplierProductSelection.length > 0 && (

            <div className="supplier_products">

              {supplierProductSelection.map(
                product => (

                  <AISupplierProductCard
                    key={product.product_id}
                    product={product}
                    selectionMode={true}
                    onSelect={() => {

                      const productName =
                        product.product_name ||
                        product.product_name_english ||
                        "";

                      if (!productName) {
                        return;
                      }

                      sendMessage(
                        `What are the sales of ${productName}?`
                      );

                    }}
                  />

                )
              )}

            </div>

          )}


          {/* =================================================
              SUPPLIER TOP SELLING PRODUCTS
          ================================================= */}

          {mode === "supplier" &&
            supplierProducts.length > 0 && (

              <div className="supplier_products">

                {supplierProducts.map(
                  product => (

                    <AISupplierProductCard
                      key={
                        product.product_id
                      }
                      product={product}
                    />

                  )
                )}

              </div>

            )}


          {/* =================================================
              SUGGESTIONS
          ================================================= */}

          {messages.length === 1 && (

            <div className="suggestions">

              <h5>
                Try asking
              </h5>


              {suggestions.map(
                (question, index) => (

                  <button
                    key={index}
                    className="suggestion_btn"
                    onClick={() =>
                      sendMessage(question)
                    }
                  >

                    {question}

                  </button>

                )
              )}

            </div>

          )}


          {/* =================================================
              LOADING
          ================================================= */}

          {loading && (

            <div className="assistant_message">
              Mahal AI is thinking...
            </div>

          )}

        </div>


        {/* =================================================
            FOOTER
        ================================================= */}

        <div className="chat_footer">

          <input
            type="text"
            placeholder="Ask Mahal AI..."
            value={message}

            onChange={(e) =>
              setMessage(
                e.target.value
              )
            }

            onKeyDown={(e) => {

              if (
                e.key === "Enter" &&
                !loading
              ) {

                sendMessage();

              }

            }}

            disabled={loading}
          />


          <button
            onClick={sendMessage}
            disabled={
              loading ||
              !message.trim()
            }
          >

            {loading
              ? "..."
              : "Send"}

          </button>

        </div>


      </div>

    </div>

  );

};


export default ChatDrawer;